/**
 * La pila de cursores de un listado.
 *
 * `BFF_BO.md` §3.6: **por cursor, nunca por número de página.** Los listados
 * cambian mientras se leen —el administrativo que da de baja a alguien de la
 * página 2 desplaza la 3— y con números se salta una fila sin que nadie lo vea.
 * Y no hay total: contar 4.000 alumnos en cada página cuesta más que devolverlos.
 *
 * Lo que un cursor no da gratis es **volver atrás**, porque solo apunta hacia
 * delante. La solución es acordarse del camino: una pila donde la posición 0 es
 * la primera página —cursor `null`— y cada «Siguiente» apila el `next_cursor` que
 * vino con la página actual.
 *
 * Está aquí y no dentro del componente porque va a repetirse en cada listado del
 * panel, y porque así se puede probar sin pintar nada.
 */

/** `null` en la primera posición: la primera página no lleva cursor. */
export type PilaDeCursores = readonly (string | null)[];

export const PRIMERA: PilaDeCursores = [null];

/** El cursor de la página que se está viendo. */
export const cursorActual = (pila: PilaDeCursores): string | null =>
  pila[pila.length - 1] ?? null;

/** 1 para la primera. Se enseña, pero nunca como «3 de 12»: no hay total. */
export const numeroDePagina = (pila: PilaDeCursores): number => pila.length;

export const esLaPrimera = (pila: PilaDeCursores): boolean => pila.length === 1;

/**
 * Avanza. Si no hay cursor siguiente, **no se mueve**.
 *
 * Apilar un `null` sería volver a la primera página sin decirlo, que es el fallo
 * que deja a alguien pulsando «Siguiente» y viendo siempre lo mismo.
 */
export function siguiente(
  pila: PilaDeCursores,
  proximoCursor: string | null
): PilaDeCursores {
  if (!proximoCursor) return pila;
  return [...pila, proximoCursor];
}

/** Retrocede. En la primera no hace nada. */
export function anterior(pila: PilaDeCursores): PilaDeCursores {
  return pila.length <= 1 ? pila : pila.slice(0, -1);
}
